import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CalendarClock,
  FileText,
  MessagesSquare,
  Receipt,
  Workflow,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const WHAT_YOU_GET = [
  {
    icon: Building2,
    title: 'A practice, not a login',
    body: (
      'Your own organisation with your colleagues, your clients in one ' +
      'list, and everything you write about them kept private to you.'
    ),
  },
  {
    icon: Workflow,
    title: 'Somewhere to do the work',
    body: (
      'A profile for every client, the servers their automations run on, ' +
      'and the offers and hours you bill them for.'
    ),
  },
  {
    icon: FileText,
    title: 'A public page worth sending',
    body: (
      'A profile, a blog and a mailing list, on a URL you can put on a ' +
      'business card. Certified advisors also appear in the directory.'
    ),
  },
  {
    icon: MessagesSquare,
    title: 'A line to every client',
    body: (
      'One inbox for every client conversation, with files, so nothing ' +
      "important is lost in someone's personal email."
    ),
  },
  {
    icon: Receipt,
    title: 'Servers and billing handled',
    body: (
      'Your clients get their own Bailey servers on a 30-day trial and are ' +
      'invoiced monthly. You advise; we run the plumbing.'
    ),
  },
  {
    icon: BadgeCheck,
    title: 'Certification',
    body: (
      'A call with someone at BitSwan earns your basic certification and ' +
      'puts you in front of people looking for an advisor.'
    ),
  },
];

const STEPS = [
  {
    title: 'Create your account',
    body: 'Your name, your email, a password. Two minutes.',
  },
  {
    title: 'Set your practice up',
    body: 'Name your organisation and write the profile clients will read.',
  },
  {
    title: 'Start with the clients you already have',
    body:
      'You can add and work with your own clients straight away; you do ' +
      'not have to wait for us.',
  },
  {
    title: 'Book your certification call',
    body:
      'Half an hour with someone at BitSwan. Once certified, your profile ' +
      'is listed in the directory and customers can choose you.',
  },
];

export default function BecomeAnAdvisorPage() {
  return (
    <div className="min-h-screen w-full bg-background" data-testid="become-an-advisor-page">
      <section className="border-b bg-gradient-to-b from-amber-50 to-background">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center">
          <p className="text-sm font-medium uppercase tracking-wide text-amber-600">
            For consultants and integrators
          </p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
            Become a BitSwan Automation Advisor
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Bring what you know about how businesses run. We bring the platform underneath: servers, automations and invoicing, and the customers looking for someone like you.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" data-testid="become-advisor-cta">
              <Link to="/signup?as=advisor">
                Join as an advisor
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/public/advisors">See who is already listed</Link>
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Free to join. Nothing is listed publicly until you are certified.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14">
        <h2 className="text-2xl font-semibold">What you get</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WHAT_YOU_GET.map((item) => (
            <Card key={item.title}>
              <CardHeader className="pb-2">
                <item.icon className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-base">{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{item.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="border-t bg-muted/30">
        <div className="mx-auto max-w-5xl px-4 py-14">
          <h2 className="text-2xl font-semibold">How joining works</h2>
          <ol className="mt-6 space-y-5">
            {STEPS.map((step, index) => (
              <li key={step.title} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500 text-sm font-semibold text-white">
                  {index + 1}
                </span>
                <div>
                  <p className="font-medium">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-amber-600" />
              About certification
            </CardTitle>
            <CardDescription>
              Companies pick a partner from the directory, so we meet everyone in it first.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              You can join, set your practice up and work with your own clients
              today. You will not appear in the public directory, and customers
              signing up cannot choose you, until you have had a certification call with someone at BitSwan and been granted basic
              certification.
            </p>
            <p>
              You book that call from your advisor area once your profile exists. It is a conversation, not an exam.
            </p>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <Button asChild size="lg" data-testid="become-advisor-cta-bottom">
            <Link to="/signup?as=advisor">
              Join as an advisor
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
